/**
 * Debug logging for multiplayer sync issues.
 * Enable: gameLogger.enable() or localStorage.setItem('vvorms_debug', 'true')
 * Categories: gameLogger.enableCategory('Network') or settings panel
 */

import type { EventBus } from './eventBus';
import type { GamePhase } from '../logic/gameState';

export interface LogContext {
  tick: number;
  phase: GamePhase;
  currentTeamId: string | null;
  currentWormId: string | null;
  isHost: boolean;
  isMyTurn: boolean;
}

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';
export type LogCategory = 'Turn' | 'Weapon' | 'Damage' | 'Network' | 'Entity' | 'Game';

export interface LogEntry {
  timestamp: number;
  level: LogLevel;
  category: LogCategory;
  message: string;
  data?: Record<string, unknown>;
  context?: Partial<LogContext>;
}

export interface LogSettings {
  enabled: boolean;
  categories: Record<LogCategory, boolean>;
}

type ContextProvider = () => LogContext;

const DEFAULT_SETTINGS: LogSettings = {
  enabled: true,
  categories: {
    Turn: true,
    Weapon: true,
    Damage: true,
    Network: false,
    Entity: false,
    Game: true,
  },
};

const STORAGE_KEY = 'vvorms_log_settings';

export class GameLogger {
  private settings: LogSettings;
  private contextProvider: ContextProvider | null = null;
  private logBuffer: LogEntry[] = [];
  private readonly MAX_BUFFER_SIZE = 1000;
  private unsubscribers: Array<() => void> = [];

  constructor() {
    this.settings = this.loadSettings();
  }

  private loadSettings(): LogSettings {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          ...DEFAULT_SETTINGS,
          ...parsed,
          categories: { ...DEFAULT_SETTINGS.categories, ...parsed.categories },
        };
      }
    } catch {
      /* noop */
    }
    return { ...DEFAULT_SETTINGS };
  }

  private saveSettings(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch {
      /* noop */
    }
  }

  setContextProvider(provider: ContextProvider): void {
    this.contextProvider = provider;
  }

  enable(): void {
    this.settings.enabled = true;
    this.saveSettings();
    console.log('[GameLogger] Enabled');
  }

  disable(): void {
    this.settings.enabled = false;
    this.saveSettings();
    console.log('[GameLogger] Disabled');
  }

  isEnabled(): boolean {
    return this.settings.enabled;
  }

  enableCategory(category: LogCategory): void {
    this.settings.categories[category] = true;
    this.saveSettings();
  }

  disableCategory(category: LogCategory): void {
    this.settings.categories[category] = false;
    this.saveSettings();
  }

  isCategoryEnabled(category: LogCategory): boolean {
    return this.settings.categories[category] ?? false;
  }

  getSettings(): LogSettings {
    return { ...this.settings, categories: { ...this.settings.categories } };
  }

  updateSettings(settings: Partial<LogSettings>): void {
    if (settings.enabled !== undefined) {
      this.settings.enabled = settings.enabled;
    }
    if (settings.categories) {
      this.settings.categories = { ...this.settings.categories, ...settings.categories };
    }
    this.saveSettings();
  }

  private getContext(): Partial<LogContext> {
    if (!this.contextProvider) return {};
    try {
      return this.contextProvider();
    } catch {
      return {};
    }
  }

  private formatContext(ctx: Partial<LogContext>): string {
    const parts: string[] = [];
    if (ctx.tick !== undefined) parts.push(`T${ctx.tick}`);
    if (ctx.phase) parts.push(ctx.phase);
    if (ctx.currentTeamId) parts.push(ctx.currentTeamId);
    if (ctx.currentWormId) parts.push(ctx.currentWormId.split('_').pop() || '');
    if (ctx.isHost !== undefined) parts.push(ctx.isHost ? 'HOST' : 'CLIENT');
    if (ctx.isMyTurn !== undefined) parts.push(ctx.isMyTurn ? 'MY_TURN' : 'WAITING');
    return parts.length > 0 ? `[${parts.join('|')}]` : '';
  }

  private log(
    level: LogLevel,
    category: LogCategory,
    message: string,
    data?: Record<string, unknown>,
  ): void {
    const context = this.getContext();
    const entry: LogEntry = {
      timestamp: Date.now(),
      level,
      category,
      message,
      data,
      context,
    };

    this.logBuffer.push(entry);
    if (this.logBuffer.length > this.MAX_BUFFER_SIZE) {
      this.logBuffer.shift();
    }

    if (!this.settings.enabled || !this.settings.categories[category]) return;

    const contextStr = this.formatContext(context);
    const prefix = `[VVorms:${category}]${contextStr}`;
    const dataStr = data ? ` ${JSON.stringify(data)}` : '';

    switch (level) {
      case 'debug':
        console.debug(`${prefix} ${message}${dataStr}`);
        break;
      case 'info':
        console.log(`${prefix} ${message}${dataStr}`);
        break;
      case 'warn':
        console.warn(`${prefix} ${message}${dataStr}`);
        break;
      case 'error':
        console.error(`${prefix} ${message}${dataStr}`);
        break;
    }
  }

  turnStarted(
    teamId: string,
    teamName: string,
    wormId: string,
    wormName: string,
    turnNumber: number,
  ): void {
    this.log('info', 'Turn', `TURN STARTED: ${teamName}/${wormName}`, {
      teamId,
      wormId,
      turnNumber,
    });
  }

  turnEnded(teamId: string, wormId: string, reason: string): void {
    this.log('info', 'Turn', `TURN ENDED: ${reason}`, { teamId, wormId, reason });
  }

  phaseChanged(from: string, to: string): void {
    this.log('info', 'Turn', `PHASE: ${from} -> ${to}`, { from, to });
  }

  turnTimerUpdate(remaining: number): void {
    if (Math.floor(remaining) % 5 === 0 && remaining === Math.floor(remaining)) {
      this.log('debug', 'Turn', `Timer: ${remaining}s`);
    }
  }

  turnAdvanced(fromTeam: string, toTeam: string, fromWorm: string, toWorm: string): void {
    this.log('info', 'Turn', `ADVANCE: ${fromTeam}/${fromWorm} -> ${toTeam}/${toWorm}`, {
      fromTeam,
      toTeam,
      fromWorm,
      toWorm,
    });
  }

  weaponSelected(weaponId: string, teamId: string, wormName: string): void {
    this.log('info', 'Weapon', `SELECTED: ${weaponId} by ${wormName}`, { weaponId, teamId });
  }

  weaponFired(
    weaponId: string,
    wormName: string,
    teamName: string,
    angle: number,
    power: number,
    target?: { x: number; y: number },
    source?: 'local' | 'remote',
  ): void {
    this.log('info', 'Weapon', `FIRED: ${weaponId} by ${teamName}/${wormName}`, {
      weaponId,
      angle: Math.round(angle),
      power: Math.round(power),
      target,
      source: source || 'local',
    });
  }

  weaponHit(
    weaponId: string,
    targetWormName: string,
    targetTeamName: string,
    damage: number,
  ): void {
    this.log(
      'info',
      'Weapon',
      `HIT: ${weaponId} -> ${targetTeamName}/${targetWormName} for ${damage}dmg`,
      {
        weaponId,
        targetWormName,
        damage,
      },
    );
  }

  weaponMiss(weaponId: string): void {
    this.log('debug', 'Weapon', `MISS: ${weaponId}`);
  }

  damageDealt(
    targetWormName: string,
    targetTeamName: string,
    damage: number,
    newHealth: number,
    source: string,
  ): void {
    this.log(
      'info',
      'Damage',
      `${targetTeamName}/${targetWormName}: -${damage}hp (now ${newHealth}hp) from ${source}`,
      {
        damage,
        newHealth,
        source,
      },
    );
  }

  damageResolved(wormName: string, teamName: string, damage: number, died: boolean): void {
    const status = died ? 'DIED' : 'survived';
    this.log('info', 'Damage', `RESOLVED: ${teamName}/${wormName} took ${damage}dmg, ${status}`, {
      damage,
      died,
    });
  }

  wormDied(wormName: string, teamName: string, cause: string): void {
    this.log('warn', 'Damage', `DEATH: ${teamName}/${wormName} (${cause})`, { cause });
  }

  networkStateReceived(tick: number, phase: string, currentTeamIndex: number): void {
    this.log(
      'debug',
      'Network',
      `STATE RECV: tick=${tick} phase=${phase} teamIdx=${currentTeamIndex}`,
      {
        tick,
        phase,
        currentTeamIndex,
      },
    );
  }

  networkTurnEndReceived(): void {
    this.log('info', 'Network', 'TURN_END received from host');
  }

  networkTurnEndAckSent(): void {
    this.log('debug', 'Network', 'TURN_END_ACK sent');
  }

  networkTurnEndAckReceived(playerId: string, pending: number): void {
    this.log('debug', 'Network', `TURN_END_ACK from ${playerId}, ${pending} pending`, {
      playerId,
      pending,
    });
  }

  networkStateBroadcast(tick: number, forced: boolean): void {
    this.log('debug', 'Network', `STATE BROADCAST: tick=${tick}${forced ? ' (forced)' : ''}`, {
      tick,
      forced,
    });
  }

  networkSyncMismatch(expected: string, actual: string, reason: string): void {
    this.log(
      'warn',
      'Network',
      `SYNC MISMATCH: ${reason} (expected=${expected}, actual=${actual})`,
      {
        expected,
        actual,
        reason,
      },
    );
  }

  networkWormNotFound(wormId: string): void {
    this.log('warn', 'Network', `Worm not found: ${wormId}`, { wormId });
  }

  networkEntitySynced(type: string, id: string, action: 'created' | 'updated' | 'destroyed'): void {
    this.log('debug', 'Network', `ENTITY SYNC: ${type}#${id.split('_').pop()} ${action}`, {
      type,
      id,
      action,
    });
  }

  entitySpawned(type: string, id: string, x: number, y: number, ownerId?: string): void {
    this.log('debug', 'Entity', `SPAWNED: ${type}#${id} at (${Math.round(x)},${Math.round(y)})`, {
      type,
      id,
      x: Math.round(x),
      y: Math.round(y),
      ownerId,
    });
  }

  entityDestroyed(type: string, id: string, reason: string): void {
    this.log('debug', 'Entity', `DESTROYED: ${type}#${id} (${reason})`, { type, id, reason });
  }

  entityExploded(
    type: string,
    id: string,
    x: number,
    y: number,
    radius: number,
    damage: number,
  ): void {
    this.log(
      'info',
      'Entity',
      `EXPLODED: ${type}#${id} at (${Math.round(x)},${Math.round(y)}) r=${radius} dmg=${damage}`,
      {
        type,
        id,
        x: Math.round(x),
        y: Math.round(y),
        radius,
        damage,
      },
    );
  }

  gameStarted(teamCount: number, isHost: boolean): void {
    this.log('info', 'Game', `STARTED: ${teamCount} teams, ${isHost ? 'HOST' : 'CLIENT'}`, {
      teamCount,
      isHost,
    });
  }

  gameEnded(winnerName: string | null): void {
    this.log('info', 'Game', `ENDED: Winner = ${winnerName || 'DRAW'}`, { winnerName });
  }

  getLogBuffer(): LogEntry[] {
    return [...this.logBuffer];
  }

  exportLogs(): string {
    return JSON.stringify(this.logBuffer, null, 2);
  }

  clearBuffer(): void {
    this.logBuffer = [];
  }

  subscribeToEventBus(eventBus: EventBus): void {
    this.unsubscribers.forEach((unsub) => unsub());
    this.unsubscribers = [];

    this.unsubscribers.push(
      eventBus.on('turn:phaseChanged', (data) => {
        this.phaseChanged(data.from, data.to);
      }),
    );

    this.unsubscribers.push(
      eventBus.on('weapon:fired', (data) => {
        this.weaponFired(
          data.weaponId,
          data.wormId,
          data.teamId,
          data.angle,
          data.power,
          data.target,
          data.source,
        );
      }),
    );

    this.unsubscribers.push(
      eventBus.on('worm:died', (data) => {
        this.wormDied(data.wormId, data.teamId, data.cause);
      }),
    );

    this.unsubscribers.push(
      eventBus.on('worm:damageResolved', (data) => {
        this.damageResolved(data.wormId, data.teamId, data.damage, data.died);
      }),
    );

    this.unsubscribers.push(
      eventBus.on('entity:spawned', (data) => {
        this.entitySpawned(data.type, data.id, data.position.x, data.position.y, data.ownerId);
      }),
    );

    this.unsubscribers.push(
      eventBus.on('entity:destroyed', (data) => {
        this.entityDestroyed(data.type, data.id, data.reason);
      }),
    );

    this.unsubscribers.push(
      eventBus.on('entity:exploded', (data) => {
        this.entityExploded(
          data.type,
          data.id,
          data.position.x,
          data.position.y,
          data.radius,
          data.maxDamage,
        );
      }),
    );
  }

  dispose(): void {
    this.unsubscribers.forEach((unsub) => unsub());
    this.unsubscribers = [];
  }
}

export const gameLogger = new GameLogger();

if (typeof window !== 'undefined') {
  (window as unknown as { gameLogger: GameLogger }).gameLogger = gameLogger;
}
